/**
 * Sysadmin Memory observability — TanStack Query hooks (B2-2).
 *
 * Wraps:
 *   GET  /admin/platform/tenants/:tenantId/memory/stats     → useTenantMemoryStats
 *   GET  /admin/platform/tenants/:tenantId/memory/episodes  → useTenantMemoryEpisodes
 *   GET  /admin/platform/tenants/:tenantId/memory/memories  → useTenantPersonMemorySummary
 *        (what a sysadmin may know of one person's memories — how many, under
 *        which labels, last updated; NEVER the content: decisions D1 and Ask
 *        Argus B5, only the person reads their memories)
 *   POST /admin/platform/tenants/:tenantId/memory/purge     → useBulkPurgeUserMemory
 *
 * Sysadmin-only endpoints. Audience gate is handled by the apiClient
 * automatically attaching the sysadmin token; the route layer enforces
 * read:memory_observability / purge:memory_observability.
 *
 * Per the spec the Bulk Purge mutation invalidates the stats + browse
 * queries on success so the dashboard reflects the new zero counts.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type { ExportControl, Sensitivity } from '@argusai/shared';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface MemoryStats {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  profile: {
    count: number;
    totalChars: number;
    avgChars: number;
    oldestAt: string | null;
    newestAt: string | null;
  };
  episode: {
    count: number;
    withEmbedding: number;
    totalChars: number;
    avgChars: number;
    oldestAt: string | null;
    newestAt: string | null;
  };
  distinctUsersMax: number;
}

export interface TopUser {
  userId: string;
  userEmail: string | null;
  profileCount: number;
  episodeCount: number;
  totalCount: number;
}

export interface TopTag {
  tag: string;
  count: number;
}

export interface MemoryStatsResponse {
  stats: MemoryStats;
  topUsers: TopUser[];
  topTags: TopTag[];
  collectedAt: string;
}

export interface EpisodeRow {
  id: string;
  userId: string;
  userEmail: string | null;
  agentSlug: string | null;
  content: string;
  category: string;
  tags: string[];
  classificationAtWrite: {
    sensitivity?: string;
    exportControl?: string;
    [k: string]: unknown;
  };
  conversationId: string | null;
  hasEmbedding: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

/** What a sysadmin may know of one person's memories — never their content. */
export interface PersonMemorySummary {
  userId: string;
  count: number;
  labels: Array<{ sensitivity: string; exportControl: string; count: number }>;
  lastUpdatedAt: string | null;
}

export interface EpisodesResponse {
  items: EpisodeRow[];
  nextCursor: string | null;
}

export interface PersonMemorySummaryResponse {
  summary: PersonMemorySummary;
}

export interface EpisodeFilters {
  userId?: string;
  tag?: string;
  sensitivity?: Sensitivity;
  exportControl?: ExportControl;
  cursor?: string;
  limit?: number;
}

// ─── Stats ──────────────────────────────────────────────────────────────────

/**
 * Detect whether a thrown apiClient error represents a 5xx server failure.
 * `apiClient.get` decorates the thrown Error with `(err as any).status =
 * response.status`; the message format is `Request failed: 500` (see
 * `api-client.ts:formatErrorMessage`). We check both so the gate survives
 * future formatErrorMessage changes.
 */
function isServerError(err: unknown): boolean {
  if (!err) return false;
  const status = (err as { status?: unknown }).status;
  if (typeof status === 'number' && status >= 500 && status < 600) {
    return true;
  }
  const message = (err as { message?: unknown }).message;
  if (typeof message === 'string' && /\b5\d\d\b/.test(message)) {
    return true;
  }
  return false;
}

export function useTenantMemoryStats(
  tenantId: string,
  options: { topUserLimit?: number; topTagLimit?: number } = {},
) {
  const qs = new URLSearchParams();
  if (options.topUserLimit) qs.set('topUserLimit', String(options.topUserLimit));
  if (options.topTagLimit) qs.set('topTagLimit', String(options.topTagLimit));
  const query = qs.toString();

  return useQuery<MemoryStatsResponse, Error>({
    queryKey: ['sysadmin-tenant-memory-stats', tenantId, options],
    queryFn: () =>
      apiClient.get<MemoryStatsResponse>(
        `/admin/platform/tenants/${tenantId}/memory/stats${query ? `?${query}` : ''}`,
      ),
    enabled: !!tenantId,
    // Stats are cheap; refetch on tab focus so operators see fresh numbers.
    refetchOnWindowFocus: true,
    staleTime: 15_000,
    /**
     * Round-6 P0-2: TanStack Query's default retry is 3 — meaning a real
     * 5xx (e.g. the P0-1 jsonb_array_elements_text regression) made
     * operators stare at the spinner for ~8s before the error surfaced,
     * indistinguishable from a stuck request. Stop retrying on 5xx so the
     * error renders immediately; allow ONE retry on transient/network
     * errors (status undefined or 408/429) for resilience.
     */
    retry: (failureCount, err) => {
      if (isServerError(err)) return false;
      return failureCount < 1;
    },
  });
}

// Exported for unit-test access. Not part of the public hook surface.
export const __test_isServerError = isServerError;

// ─── Episode browse ─────────────────────────────────────────────────────────

export function useTenantMemoryEpisodes(
  tenantId: string,
  filters: EpisodeFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.userId) qs.set('userId', filters.userId);
  if (filters.tag) qs.set('tag', filters.tag);
  if (filters.sensitivity) qs.set('sensitivity', filters.sensitivity);
  if (filters.exportControl) qs.set('exportControl', filters.exportControl);
  if (filters.cursor) qs.set('cursor', filters.cursor);
  if (filters.limit) qs.set('limit', String(filters.limit));
  const query = qs.toString();

  return useQuery<EpisodesResponse, Error>({
    queryKey: ['sysadmin-tenant-memory-episodes', tenantId, filters],
    queryFn: () =>
      apiClient.get<EpisodesResponse>(
        `/admin/platform/tenants/${tenantId}/memory/episodes${query ? `?${query}` : ''}`,
      ),
    enabled: !!tenantId,
  });
}

// ─── A person's memories: a summary, never content ──────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Is this a person id the route will accept? (the query waits until it is) */
export function isPersonId(value: string | undefined): value is string {
  return !!value && UUID_RE.test(value.trim());
}

export function useTenantPersonMemorySummary(tenantId: string, userId: string | undefined) {
  const id = userId?.trim();
  return useQuery<PersonMemorySummaryResponse, Error>({
    queryKey: ['sysadmin-tenant-person-memories', tenantId, id],
    queryFn: () =>
      apiClient.get<PersonMemorySummaryResponse>(
        `/admin/platform/tenants/${tenantId}/memory/memories?userId=${encodeURIComponent(id ?? '')}`,
      ),
    enabled: !!tenantId && isPersonId(id),
  });
}

// ─── Bulk purge ─────────────────────────────────────────────────────────────

export interface BulkPurgeBody {
  userId: string;
  reason: string;
  /** Always true — the UI's TypedConfirmDialog never sends false. */
  confirmed: true;
}

export interface BulkPurgeResponse {
  purged: {
    tenantId: string;
    userId: string;
    memoriesDeleted: number;
    episodesDeleted: number;
    bytesFreed: number;
  };
  auditedAt: string;
}

export function useBulkPurgeUserMemory(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<BulkPurgeResponse, Error, BulkPurgeBody>({
    mutationFn: (body) =>
      apiClient.post<BulkPurgeResponse>(
        `/admin/platform/tenants/${tenantId}/memory/purge`,
        body,
      ),
    onSuccess: () => {
      // Invalidate every memory query for this tenant — stats, episodes,
      // a person's memories all shift after a purge.
      qc.invalidateQueries({
        queryKey: ['sysadmin-tenant-memory-stats', tenantId],
      });
      qc.invalidateQueries({
        queryKey: ['sysadmin-tenant-memory-episodes', tenantId],
      });
      qc.invalidateQueries({
        queryKey: ['sysadmin-tenant-person-memories', tenantId],
      });
    },
  });
}
